import React, { useState } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppSelect from "@/features/line-sheet-sets/app-select";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  KreamBuyerInfo,
  KreamBuyerLevel,
  KreamProductType,
} from "@/features/pathpick/features/search-products/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  value: KreamBuyerInfo;
  onSave: (value: KreamBuyerInfo) => void;
}

const KREAM_BUYER_LEVEL_OPTIONS: KreamBuyerLevel[] = [1, 2, 3, 4, 5];
const KREAM_PRODUCT_TYPE_OPTIONS: KreamProductType[] = ["general", "premium"];

function PathPickSearchProductsKreamBuyerInfoModal({
  isOpen,
  onClose,
  value,
  onSave,
}: Props) {
  const { t } = useI18nHelper();
  // 조건부 마운트(열 때마다 fresh mount) 전제라 초기값 동기화 effect가 필요 없다.
  const [draft, setDraft] = useState<KreamBuyerInfo>(value);

  const levelOptions = KREAM_BUYER_LEVEL_OPTIONS.map((level) => ({
    name: t("path_pick:filter.kream_buyer_level_value", { level }),
    value: level,
  }));

  const productTypeOptions = KREAM_PRODUCT_TYPE_OPTIONS.map((productType) => ({
    name: t(`path_pick:filter.kream_product_type_${productType}`),
    value: productType,
  }));

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"720px"}
      scrollBehavior={"inside"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={
          <Flex gap={"8px"} alignItems={"center"}>
            <CustomIcon src={"user"} width={20} height={20} color={"#1272EF"} />
            <Flex flexDirection={"column"} gap={"2px"}>
              <Text textStyle={"body-md-bold"} color={"#444444"}>
                {t("path_pick:filter.kream_buyer_info_modal_title")}
              </Text>
              <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                {t("path_pick:filter.kream_buyer_info_modal_description")}
              </Text>
            </Flex>
          </Flex>
        }
      />
      <CustomApprovalModal.Body>
        <Grid templateColumns={"repeat(2, minmax(0, 1fr))"} gap={"16px"}>
          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t("path_pick:filter.kream_buyer_level")}
            </Text>
            <AppSelect<KreamBuyerLevel>
              name={t("path_pick:filter.kream_buyer_level")}
              value={draft.level}
              options={levelOptions}
              onSelect={(level) => {
                if (level) {
                  setDraft((prev) => ({ ...prev, level }));
                }
              }}
              width={"100%"}
              isSearchable={false}
              isClearable={false}
              dropdownWidth={"100%"}
            />
          </Flex>
          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t("path_pick:filter.kream_product_type")}
            </Text>
            <AppSelect<KreamProductType>
              name={t("path_pick:filter.kream_product_type")}
              value={draft.productType}
              options={productTypeOptions}
              onSelect={(productType) => {
                if (productType) {
                  setDraft((prev) => ({ ...prev, productType }));
                }
              }}
              width={"100%"}
              isSearchable={false}
              isClearable={false}
              dropdownWidth={"100%"}
            />
          </Flex>
        </Grid>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("common:cancel")}
        confirmName={t("path_pick:filter.kream_buyer_info_save")}
        onCancel={onClose}
        onConfirm={handleSave}
        confirmBtnProps={{ width: "160px" }}
      />
    </CustomApprovalModal>
  );
}

export default PathPickSearchProductsKreamBuyerInfoModal;
